import React, { useEffect, useState } from "react";
import {
  Scale, FileText, TrendingUp, Landmark, Layers, ShieldCheck, Clock,
  Package, Wrench, Download, FileSpreadsheet, FileDown, ArrowDownCircle, ArrowUpCircle,
} from "lucide-react";
import api, { API } from "@/lib/api";
import { useCompany, fmtMoney } from "@/lib/company";
import { fetchReport } from "@/lib/offlineReports";

// -------- helpers ---------------------------------------------------
function ExportBar({ pdfUrl, xlsxUrl, csvUrl, testidPrefix = "" }) {
  return (
    <div className="flex gap-2">
      {pdfUrl && (
        <a href={pdfUrl} target="_blank" rel="noreferrer"
          data-testid={`${testidPrefix}export-pdf`}
          className="btn-ghost text-xs border border-[#E2E8F0]">
          <FileDown size={13}/> PDF
        </a>
      )}
      {xlsxUrl && (
        <a href={xlsxUrl} target="_blank" rel="noreferrer"
          data-testid={`${testidPrefix}export-xlsx`}
          className="btn-ghost text-xs border border-[#E2E8F0]">
          <FileSpreadsheet size={13}/> Excel
        </a>
      )}
      {csvUrl && (
        <a href={csvUrl} target="_blank" rel="noreferrer"
          data-testid={`${testidPrefix}export-csv`}
          className="btn-ghost text-xs border border-[#E2E8F0]">
          <Download size={13}/> CSV
        </a>
      )}
    </div>
  );
}

const REPORTS = [
  { key: "trial-balance",   label: "Trial Balance",   icon: Scale,     group: "financials" },
  { key: "pnl",              label: "Profit & Loss",   icon: TrendingUp, group: "financials" },
  { key: "balance-sheet",   label: "Balance Sheet",   icon: Landmark,  group: "financials" },
  { key: "cash-flow",       label: "Cash Flow",       icon: Layers,    group: "financials" },
  { key: "day-book",        label: "Day Book",        icon: Clock,     group: "financials" },
  { key: "gstr",             label: "GSTR / VAT",     icon: ShieldCheck, group: "statutory" },
  { key: "receivables",     label: "Receivables",     icon: ArrowDownCircle, group: "outstanding" },
  { key: "payables",        label: "Payables",        icon: ArrowUpCircle,  group: "outstanding" },
  { key: "stock-summary",   label: "Stock Summary",   icon: Package,   group: "inventory" },
  { key: "godown-summary",  label: "Godown Summary",  icon: Package,   group: "inventory" },
  { key: "job-cards-summary", label: "Job Card Summary", icon: Wrench, group: "service" },
  { key: "service-profitability", label: "Service Profitability", icon: TrendingUp, group: "service" },
];

const GROUPS = [
  { key: "financials",  label: "Financials" },
  { key: "statutory",   label: "Statutory" },
  { key: "outstanding", label: "Outstanding" },
  { key: "inventory",   label: "Inventory" },
  { key: "service",     label: "Service" },
];

function api_url(path) {
  return `${API}${path}`;
}

export default function Reports() {
  const { active } = useCompany();
  const [tab, setTab] = useState("trial-balance");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [offlineInfo, setOfflineInfo] = useState(null);
  // Branch / Project drill-down dimensions (apply to TB/PnL/BS).
  const [branches, setBranches] = useState([]);
  const [projects, setProjects] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [projectId, setProjectId] = useState("");

  useEffect(() => {
    if (!active) return;
    api.get("/hierarchy/branches").then((r) => setBranches(r.data.items || [])).catch(() => setBranches([]));
    api.get("/hierarchy/projects").then((r) => setProjects(r.data.items || [])).catch(() => setProjects([]));
  }, [active]);

  const dimSupportedTabs = ["trial-balance", "pnl", "balance-sheet"];
  const supportsDim = dimSupportedTabs.includes(tab);
  const dimQuery = supportsDim
    ? [branchId && `branch_id=${branchId}`, projectId && `project_id=${projectId}`]
        .filter(Boolean).join("&")
    : "";

  useEffect(() => {
    if (!active) return;
    setLoading(true);
    setData(null);
    setOfflineInfo(null);
    // Map tab → JSON endpoint
    const path = ({
      "trial-balance":   "/vouchers/reports/trial-balance",
      "pnl":              "/vouchers/reports/pnl",
      "balance-sheet":   "/vouchers/reports/balance-sheet",
      "cash-flow":       "/vouchers/reports/cash-flow",
      "day-book":        "/vouchers/reports/day-book",
      "gstr":             "/reports/gstr",
      "receivables":     "/reports/receivables",
      "payables":        "/reports/payables",
      "stock-summary":   "/reports/stock-summary",
      "godown-summary":  "/reports/godown-summary",
      "job-cards-summary": "/reports/job-cards-summary",
      "service-profitability": "/reports/service-profitability",
    })[tab];
    const url = supportsDim && dimQuery ? `${path}?${dimQuery}` : path;
    // Only wrap the four heavy financial reports in the offline cache — the
    // rest are cheap or don't tolerate stale reads (GSTR, receivables, etc).
    const cacheableTabs = ["trial-balance", "pnl", "balance-sheet", "cash-flow"];
    if (cacheableTabs.includes(tab)) {
      fetchReport(active.id, tab, { branch_id: branchId, project_id: projectId },
        () => api.get(url).then((r) => r.data))
        .then(({ data: d, source, stale, ts }) => {
          setData(d);
          if (source === "offline") setOfflineInfo({ ts });
        })
        .catch(() => setData(null))
        .finally(() => setLoading(false));
    } else {
      api.get(url).then((r) => setData(r.data)).catch(() => setData(null))
         .finally(() => setLoading(false));
    }
  }, [active, tab, dimQuery, supportsDim, branchId, projectId]);

  const cur = active?.base_currency || "INR";
  const rep = REPORTS.find((r) => r.key === tab) || REPORTS[0];

  // Export URLs
  const dimSuffix = supportsDim && dimQuery ? `?${dimQuery}` : "";
  const exportUrls = (() => {
    switch (tab) {
      case "trial-balance":
        return { xlsxUrl: api_url(`/reports/trial-balance.xlsx${dimSuffix}`) };
      case "pnl":
        return { pdfUrl: api_url(`/vouchers/reports/pnl.pdf${dimSuffix}`),
                  xlsxUrl: api_url(`/reports/pnl.xlsx${dimSuffix}`) };
      case "balance-sheet":
        return { pdfUrl: api_url(`/vouchers/reports/balance-sheet.pdf${dimSuffix}`),
                  xlsxUrl: api_url(`/reports/balance-sheet.xlsx${dimSuffix}`) };
      case "cash-flow":
        return { xlsxUrl: api_url("/reports/cash-flow.xlsx") };
      case "gstr":
        return { xlsxUrl: api_url("/reports/gstr.xlsx"),
                  csvUrl: api_url("/reports/gstr.csv") };
      case "receivables":
        return { pdfUrl: api_url("/reports/receivables.pdf"),
                  xlsxUrl: api_url("/reports/receivables.xlsx"),
                  csvUrl: api_url("/reports/receivables.csv") };
      case "payables":
        return { pdfUrl: api_url("/reports/payables.pdf"),
                  xlsxUrl: api_url("/reports/payables.xlsx"),
                  csvUrl: api_url("/reports/payables.csv") };
      case "stock-summary":
        return { xlsxUrl: api_url("/reports/stock-summary.xlsx"),
                  csvUrl: api_url("/reports/stock-summary.csv") };
      case "job-cards-summary":
        return { xlsxUrl: api_url("/reports/job-cards-summary.xlsx") };
      case "service-profitability":
        return { xlsxUrl: api_url("/reports/service-profitability.xlsx") };
      default:
        return {};
    }
  })();

  return (
    <div className="space-y-4" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <div className="overline mb-1">Reports Suite</div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#0F172A] flex items-center gap-2">
            <rep.icon size={22} className="text-[#FF5500]"/> {rep.label}
          </h1>
          <div className="text-sm text-[#64748B] mt-1">Real-time • Audit-ready • Export to PDF / Excel / CSV</div>
        </div>
        <div className="w-full sm:w-auto overflow-x-auto -mx-1 px-1">
          <ExportBar {...exportUrls} testidPrefix={`${tab}-`}/>
        </div>
      </div>

      {offlineInfo && (
        <div
          className="rounded-md border-l-4 border-l-[#B45309] bg-[#FEF3C7]/50 px-4 py-3 flex items-start gap-3"
          data-testid="offline-report-banner"
        >
          <div className="w-8 h-8 rounded-full bg-[#B45309]/15 flex items-center justify-center shrink-0">
            <svg className="w-4 h-4 text-[#B45309]" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.518 11.591c.75 1.335-.213 2.98-1.742 2.98H3.481c-1.53 0-2.492-1.645-1.742-2.98L8.257 3.1zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
            </svg>
          </div>
          <div className="flex-1 text-sm">
            <div className="font-semibold text-[#92400E]">Offline snapshot</div>
            <div className="text-[#78350F]">
              You're viewing a cached copy captured{" "}
              <span className="font-mono-data">{new Date(offlineInfo.ts).toLocaleString()}</span>.
              Reconnect to pull the latest figures.
            </div>
          </div>
        </div>
      )}

      {/* Report navigator */}
      <div className="card-surface p-3">
        {GROUPS.map((g) => (
          <div key={g.key} className="mb-2 last:mb-0">
            <div className="overline mb-1">{g.label}</div>
            <div className="flex flex-wrap gap-1">
              {REPORTS.filter((r) => r.group === g.key).map((r) => {
                const Icon = r.icon;
                const on = tab === r.key;
                return (
                  <button key={r.key}
                    data-testid={`report-tab-${r.key}`}
                    onClick={() => setTab(r.key)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${on ? "bg-[#0F172A] border-[#0F172A] text-white" : "bg-white border-[#E2E8F0] text-[#0F172A] hover:border-[#FF5500] hover:text-[#FF5500]"}`}>
                    <Icon size={12}/> {r.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Branch / Project drill-down (financials only) */}
      {supportsDim && (branches.length > 0 || projects.length > 0) && (
        <div className="card-surface p-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3"
             data-testid="report-drilldown">
          <div className="overline">Drill down</div>
          <select
            className="input-clean text-sm"
            data-testid="drilldown-branch"
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
            style={{ maxWidth: 240 }}
          >
            <option value="">All Branches</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <select
            className="input-clean text-sm"
            data-testid="drilldown-project"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            style={{ maxWidth: 240 }}
          >
            <option value="">All Projects</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          {(branchId || projectId) && (
            <button
              data-testid="drilldown-clear"
              onClick={() => { setBranchId(""); setProjectId(""); }}
              className="btn-ghost text-xs"
            >Clear filters</button>
          )}
          <div className="text-xs text-[#94A3B8] sm:ml-auto">
            Opening balances are excluded when a dimension filter is active.
          </div>
        </div>
      )}

      <div>
        {loading && <div className="text-sm text-[#64748B]">Loading…</div>}
        {!loading && !data && <div className="text-sm text-[#64748B]">No data.</div>}
        {!loading && data && renderReport(tab, data, cur)}
      </div>
    </div>
  );
}

function renderReport(tab, data, cur) {
  switch (tab) {
    case "trial-balance": return <TrialBalance data={data} cur={cur}/>;
    case "pnl":            return <PnL data={data} cur={cur}/>;
    case "balance-sheet": return <BalanceSheet data={data} cur={cur}/>;
    case "cash-flow":     return <CashFlow data={data} cur={cur}/>;
    case "day-book":      return <DayBook data={data} cur={cur}/>;
    case "gstr":           return <GSTR data={data} cur={cur}/>;
    case "receivables":
    case "payables":      return <Aging data={data} cur={cur}/>;
    case "stock-summary": return <StockSummary data={data} cur={cur}/>;
    case "godown-summary": return <GodownSummary data={data} cur={cur}/>;
    case "job-cards-summary": return <JobCardsSummary data={data} cur={cur}/>;
    case "service-profitability": return <ServiceProfitability data={data} cur={cur}/>;
    default: return null;
  }
}

// -------- Individual report views ---------------------------------------------------
function TrialBalance({ data, cur }) {
  return (
    <div className="card-surface p-4" data-testid="trial-balance-report">
      <table className="table-clean">
        <thead><tr><th>Ledger</th><th>Group</th><th className="text-right">Debit</th><th className="text-right">Credit</th></tr></thead>
        <tbody>
          {(data.rows || []).filter((r) => r.dr > 0 || r.cr > 0).map((r) => (
            <tr key={r.ledger_id}>
              <td className="font-medium">{r.name}</td>
              <td className="text-xs text-[#64748B]">{r.group_name}</td>
              <td className="text-right font-mono-data">{r.dr ? fmtMoney(r.dr, cur) : "—"}</td>
              <td className="text-right font-mono-data">{r.cr ? fmtMoney(r.cr, cur) : "—"}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="font-bold">
            <td colSpan={2} className="text-right">Total</td>
            <td className="text-right font-mono-data" data-testid="tb-total-dr">{fmtMoney(data.totals.dr, cur)}</td>
            <td className="text-right font-mono-data" data-testid="tb-total-cr">{fmtMoney(data.totals.cr, cur)}</td>
          </tr>
          {Math.abs(data.totals.diff) > 0.01 && (
            <tr><td colSpan={4} className="text-right text-[#DC2626] font-mono-data">Diff: {fmtMoney(data.totals.diff, cur)}</td></tr>
          )}
        </tfoot>
      </table>
    </div>
  );
}

function PnL({ data, cur }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="pnl-report">
      <div className="card-surface p-4">
        <div className="overline mb-2">Income</div>
        {(data.income || []).length === 0 && <div className="text-sm text-[#64748B]">No income yet.</div>}
        {(data.income || []).map((r) => (
          <div key={r.ledger_id} className="flex justify-between py-1 border-b border-[#E2E8F0]">
            <span className="text-sm">{r.name}</span>
            <span className="font-mono-data">{fmtMoney(r.amount, cur)}</span>
          </div>
        ))}
        <div className="flex justify-between font-bold pt-2 border-t border-[#0F172A] mt-2">
          <span>Total Income</span>
          <span className="font-mono-data text-[#16A34A]">{fmtMoney(data.totals.income, cur)}</span>
        </div>
      </div>
      <div className="card-surface p-4">
        <div className="overline mb-2">Expenses</div>
        {(data.expense || []).length === 0 && <div className="text-sm text-[#64748B]">No expenses yet.</div>}
        {(data.expense || []).map((r) => (
          <div key={r.ledger_id} className="flex justify-between py-1 border-b border-[#E2E8F0]">
            <span className="text-sm">{r.name}</span>
            <span className="font-mono-data">{fmtMoney(r.amount, cur)}</span>
          </div>
        ))}
        <div className="flex justify-between font-bold pt-2 border-t border-[#0F172A] mt-2">
          <span>Total Expenses</span>
          <span className="font-mono-data text-[#DC2626]">{fmtMoney(data.totals.expense, cur)}</span>
        </div>
      </div>
      <div className="card-surface p-4 md:col-span-2">
        <div className="flex justify-between text-lg font-bold">
          <span>Net Profit / (Loss)</span>
          <span className={`font-mono-data ${data.totals.net_profit >= 0 ? "text-[#16A34A]" : "text-[#DC2626]"}`} data-testid="pnl-net">
            {fmtMoney(data.totals.net_profit, cur)}
          </span>
        </div>
      </div>
    </div>
  );
}

function BalanceSheet({ data, cur }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="balance-sheet-report">
      <div className="card-surface p-4">
        <div className="overline mb-2">Assets</div>
        {(data.assets || []).map((r) => (
          <div key={r.ledger_id} className="flex justify-between py-1 border-b border-[#E2E8F0]">
            <span className="text-sm">{r.name}</span>
            <span className="font-mono-data">{fmtMoney(r.amount, cur)}</span>
          </div>
        ))}
        <div className="flex justify-between font-bold pt-2 border-t border-[#0F172A] mt-2">
          <span>Total Assets</span>
          <span className="font-mono-data" data-testid="bs-assets">{fmtMoney(data.totals.assets, cur)}</span>
        </div>
      </div>
      <div className="card-surface p-4">
        <div className="overline mb-2">Liabilities & Equity</div>
        {(data.liabilities || []).map((r) => (
          <div key={r.ledger_id} className="flex justify-between py-1 border-b border-[#E2E8F0]">
            <span className="text-sm">{r.name}</span>
            <span className="font-mono-data">{fmtMoney(r.amount, cur)}</span>
          </div>
        ))}
        {(data.equity || []).map((r) => (
          <div key={r.ledger_id} className="flex justify-between py-1 border-b border-[#E2E8F0]">
            <span className="text-sm">{r.name}</span>
            <span className="font-mono-data">{fmtMoney(r.amount, cur)}</span>
          </div>
        ))}
        <div className="flex justify-between py-1 border-b border-[#E2E8F0] italic text-[#64748B]">
          <span className="text-sm">Current Period Profit</span>
          <span className="font-mono-data">{fmtMoney(data.current_period_profit, cur)}</span>
        </div>
        <div className="flex justify-between font-bold pt-2 border-t border-[#0F172A] mt-2">
          <span>Total Liabilities & Equity</span>
          <span className="font-mono-data" data-testid="bs-liab-eq">{fmtMoney(data.totals.liabilities_and_equity, cur)}</span>
        </div>
      </div>
    </div>
  );
}

function CashFlow({ data, cur }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="cash-flow-report">
      <div className="card-surface p-4">
        <div className="overline mb-2 text-[#16A34A]">Inflows</div>
        {(data.inflows || []).length === 0 && <div className="text-sm text-[#64748B]">None.</div>}
        {(data.inflows || []).map((r) => (
          <div key={r.group} className="flex justify-between py-1 border-b border-[#E2E8F0]">
            <span className="text-sm">{r.group}</span>
            <span className="font-mono-data">{fmtMoney(r.amount, cur)}</span>
          </div>
        ))}
        <div className="flex justify-between font-bold pt-2 border-t border-[#16A34A] mt-2">
          <span>Total Inflow</span>
          <span className="font-mono-data text-[#16A34A]">{fmtMoney(data.totals.in, cur)}</span>
        </div>
      </div>
      <div className="card-surface p-4">
        <div className="overline mb-2 text-[#DC2626]">Outflows</div>
        {(data.outflows || []).length === 0 && <div className="text-sm text-[#64748B]">None.</div>}
        {(data.outflows || []).map((r) => (
          <div key={r.group} className="flex justify-between py-1 border-b border-[#E2E8F0]">
            <span className="text-sm">{r.group}</span>
            <span className="font-mono-data">{fmtMoney(r.amount, cur)}</span>
          </div>
        ))}
        <div className="flex justify-between font-bold pt-2 border-t border-[#DC2626] mt-2">
          <span>Total Outflow</span>
          <span className="font-mono-data text-[#DC2626]">{fmtMoney(data.totals.out, cur)}</span>
        </div>
      </div>
      <div className="card-surface p-4">
        <div className="overline mb-2">Internal Transfers (contra)</div>
        {(data.transfers || []).length === 0 && <div className="text-sm text-[#64748B]">None.</div>}
        {(data.transfers || []).map((r) => (
          <div key={r.group} className="flex justify-between py-1 border-b border-[#E2E8F0]">
            <span className="text-sm">{r.group}</span>
            <span className="font-mono-data">{fmtMoney(r.amount, cur)}</span>
          </div>
        ))}
        <div className="flex justify-between font-bold pt-2 border-t border-[#0F172A] mt-2">
          <span>Transfers</span>
          <span className="font-mono-data">{fmtMoney(data.totals.transfers, cur)}</span>
        </div>
      </div>
      <div className="md:col-span-3 card-surface p-4 text-center">
        <span className="overline">Net Cash</span>
        <div className={`text-2xl font-bold font-mono-data ${data.totals.net >= 0 ? "text-[#16A34A]" : "text-[#DC2626]"}`} data-testid="cash-flow-net">
          {fmtMoney(data.totals.net, cur)}
        </div>
      </div>
    </div>
  );
}

function DayBook({ data, cur }) {
  return (
    <div className="card-surface p-4" data-testid="day-book-report">
      {(data.vouchers || []).length === 0 ? (
        <div className="text-sm text-[#64748B] text-center py-6">No vouchers yet.</div>
      ) : (
        <table className="table-clean">
          <thead><tr><th>Date</th><th>Type</th><th>Number</th><th>Narration</th><th className="text-right">Total</th></tr></thead>
          <tbody>
            {data.vouchers.map((v) => (
              <tr key={v.id} data-testid={`daybook-row-${v.id}`}>
                <td className="font-mono-data text-xs">{v.date}</td>
                <td><span className="chip chip-info text-[10px] uppercase">{v.type}</span></td>
                <td className="font-mono-data text-[#1E3A8A]">{v.number}</td>
                <td className="text-sm">{v.narration}</td>
                <td className="text-right font-mono-data font-semibold">{fmtMoney(v.total, cur)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function GSTR({ data, cur }) {
  return (
    <div className="card-surface p-4" data-testid="gstr-report">
      <table className="table-clean">
        <thead><tr>
          <th>Tax Rate %</th><th className="text-right">Taxable</th>
          <th className="text-right">CGST</th><th className="text-right">SGST</th>
          <th className="text-right">IGST</th><th className="text-right">VAT</th>
          <th className="text-right">Total</th>
        </tr></thead>
        <tbody>
          {(data.rows || []).map((r) => (
            <tr key={r.rate}>
              <td className="font-mono-data">{r.rate}%</td>
              <td className="text-right font-mono-data">{fmtMoney(r.taxable, cur)}</td>
              <td className="text-right font-mono-data">{fmtMoney(r.cgst, cur)}</td>
              <td className="text-right font-mono-data">{fmtMoney(r.sgst, cur)}</td>
              <td className="text-right font-mono-data">{fmtMoney(r.igst, cur)}</td>
              <td className="text-right font-mono-data">{fmtMoney(r.vat, cur)}</td>
              <td className="text-right font-mono-data font-semibold">{fmtMoney(r.total, cur)}</td>
            </tr>
          ))}
          {(data.rows || []).length === 0 && (
            <tr><td colSpan={7} className="text-center text-[#64748B] py-6">No outward supplies in this period.</td></tr>
          )}
        </tbody>
        <tfoot>
          <tr className="font-bold">
            <td className="text-right">TOTAL</td>
            <td className="text-right font-mono-data">{fmtMoney(data.totals.taxable, cur)}</td>
            <td className="text-right font-mono-data">{fmtMoney(data.totals.cgst, cur)}</td>
            <td className="text-right font-mono-data">{fmtMoney(data.totals.sgst, cur)}</td>
            <td className="text-right font-mono-data">{fmtMoney(data.totals.igst, cur)}</td>
            <td className="text-right font-mono-data">{fmtMoney(data.totals.vat, cur)}</td>
            <td className="text-right font-mono-data" data-testid="gstr-total">{fmtMoney(data.totals.total, cur)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function Aging({ data, cur }) {
  return (
    <div className="card-surface p-4" data-testid="aging-report">
      <table className="table-clean">
        <thead><tr>
          <th>Party</th><th>Phone</th>
          <th className="text-right">0-30</th><th className="text-right">31-60</th>
          <th className="text-right">61-90</th><th className="text-right">91-180</th>
          <th className="text-right">180+</th><th className="text-right">Outstanding</th>
        </tr></thead>
        <tbody>
          {(data.parties || []).length === 0 && (
            <tr><td colSpan={8} className="text-center text-[#64748B] py-6">No outstanding amounts.</td></tr>
          )}
          {(data.parties || []).map((p) => (
            <tr key={p.party_id}>
              <td className="font-medium">{p.name}</td>
              <td className="text-xs font-mono-data text-[#64748B]">{p.phone}</td>
              {["0-30","31-60","61-90","91-180","180+"].map((k) => (
                <td key={k} className="text-right font-mono-data">
                  {p.buckets[k] ? fmtMoney(p.buckets[k], cur) : "—"}
                </td>
              ))}
              <td className="text-right font-mono-data font-semibold text-[#FF5500]">
                {fmtMoney(p.outstanding, cur)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="font-bold">
            <td colSpan={2} className="text-right">TOTAL</td>
            {["0-30","31-60","61-90","91-180","180+"].map((k) => (
              <td key={k} className="text-right font-mono-data">
                {data.totals.buckets[k] ? fmtMoney(data.totals.buckets[k], cur) : "—"}
              </td>
            ))}
            <td className="text-right font-mono-data" data-testid="aging-outstanding">{fmtMoney(data.totals.outstanding, cur)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function StockSummary({ data, cur }) {
  return (
    <div className="card-surface p-4" data-testid="stock-summary-report">
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="border border-[#E2E8F0] rounded-lg p-3">
          <div className="overline">Items</div>
          <div className="text-lg font-bold font-mono-data">{data.totals.item_count}</div>
        </div>
        <div className="border border-[#E2E8F0] rounded-lg p-3">
          <div className="overline">Stock Value</div>
          <div className="text-lg font-bold font-mono-data">{fmtMoney(data.totals.value, cur)}</div>
        </div>
        <div className="border border-[#F59E0B]/40 bg-[#FEF3C7]/40 rounded-lg p-3">
          <div className="overline text-[#B45309]">Low Stock</div>
          <div className="text-lg font-bold font-mono-data text-[#B45309]">{data.totals.low_stock_count}</div>
        </div>
      </div>
      <table className="table-clean">
        <thead><tr>
          <th>Item</th><th>SKU</th><th>HSN</th><th>Godown</th>
          <th className="text-right">Qty</th><th className="text-right">Cost</th>
          <th className="text-right">Sell</th><th className="text-right">Reorder</th>
          <th className="text-right">Value</th>
        </tr></thead>
        <tbody>
          {(data.rows || []).length === 0 && (
            <tr><td colSpan={9} className="text-center text-[#64748B] py-6">No items yet.</td></tr>
          )}
          {(data.rows || []).map((r) => (
            <tr key={r.item_id} className={r.is_low ? "bg-[#FEF3C7]/40" : ""}>
              <td className="font-medium">{r.name}</td>
              <td className="font-mono-data text-xs">{r.sku}</td>
              <td className="font-mono-data text-xs">{r.hsn}</td>
              <td className="text-xs">{r.godown}</td>
              <td className="text-right font-mono-data">{r.qty} {r.unit}</td>
              <td className="text-right font-mono-data">{fmtMoney(r.cost_price, cur)}</td>
              <td className="text-right font-mono-data">{fmtMoney(r.sell_price, cur)}</td>
              <td className="text-right font-mono-data">{r.reorder_level}</td>
              <td className="text-right font-mono-data font-semibold">{fmtMoney(r.value, cur)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GodownSummary({ data, cur }) {
  return (
    <div className="card-surface p-4" data-testid="godown-summary-report">
      <table className="table-clean">
        <thead><tr><th>Godown</th><th className="text-right">Items</th><th className="text-right">Qty</th><th className="text-right">Stock Value</th></tr></thead>
        <tbody>
          {(data.rows || []).length === 0 && (
            <tr><td colSpan={4} className="text-center text-[#64748B] py-6">No godowns configured.</td></tr>
          )}
          {(data.rows || []).map((r) => (
            <tr key={r.godown}>
              <td className="font-medium">{r.godown}</td>
              <td className="text-right font-mono-data">{r.items}</td>
              <td className="text-right font-mono-data">{r.qty}</td>
              <td className="text-right font-mono-data font-semibold">{fmtMoney(r.value, cur)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function JobCardsSummary({ data, cur }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="job-cards-summary-report">
      <div className="card-surface p-4">
        <div className="overline mb-2">By Industry</div>
        <table className="table-clean">
          <thead><tr><th>Industry</th><th className="text-right">Count</th><th className="text-right">Revenue</th></tr></thead>
          <tbody>
            {(data.by_industry || []).length === 0 && <tr><td colSpan={3} className="text-center text-[#64748B] py-6">No job cards yet.</td></tr>}
            {(data.by_industry || []).map((r) => (
              <tr key={r.industry}>
                <td className="font-medium capitalize">{r.industry.replace("_", " ")}</td>
                <td className="text-right font-mono-data">{r.count}</td>
                <td className="text-right font-mono-data font-semibold">{fmtMoney(r.revenue, cur)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="card-surface p-4">
        <div className="overline mb-2">By Status</div>
        <table className="table-clean">
          <thead><tr><th>Status</th><th className="text-right">Count</th></tr></thead>
          <tbody>
            {(data.by_status || []).map((r) => (
              <tr key={r.status}>
                <td className="capitalize">{r.status.replace("_", " ")}</td>
                <td className="text-right font-mono-data">{r.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="md:col-span-2 card-surface p-4 flex justify-between text-lg font-bold">
        <span>Total Revenue</span>
        <span className="font-mono-data text-[#FF5500]" data-testid="jc-summary-revenue">{fmtMoney(data.totals.revenue, cur)}</span>
      </div>
    </div>
  );
}

function ServiceProfitability({ data, cur }) {
  return (
    <div className="card-surface p-4" data-testid="service-profitability-report">
      <div className="grid grid-cols-4 gap-3 mb-4">
        <div className="border border-[#E2E8F0] rounded-lg p-3">
          <div className="overline">Revenue</div>
          <div className="text-base font-bold font-mono-data text-[#16A34A]">{fmtMoney(data.totals.revenue, cur)}</div>
        </div>
        <div className="border border-[#E2E8F0] rounded-lg p-3">
          <div className="overline">Est. Cost</div>
          <div className="text-base font-bold font-mono-data text-[#DC2626]">{fmtMoney(data.totals.estimated_cost, cur)}</div>
        </div>
        <div className="border border-[#E2E8F0] rounded-lg p-3">
          <div className="overline">Profit</div>
          <div className="text-base font-bold font-mono-data text-[#FF5500]">{fmtMoney(data.totals.profit, cur)}</div>
        </div>
        <div className="border border-[#E2E8F0] rounded-lg p-3">
          <div className="overline">Margin</div>
          <div className="text-base font-bold font-mono-data">{data.totals.margin_pct}%</div>
        </div>
      </div>
      <table className="table-clean">
        <thead><tr>
          <th>Job Card</th><th>Industry</th><th>Customer</th><th>Date</th>
          <th className="text-right">Revenue</th><th className="text-right">Cost</th>
          <th className="text-right">Profit</th><th className="text-right">Margin</th>
        </tr></thead>
        <tbody>
          {(data.rows || []).length === 0 && <tr><td colSpan={8} className="text-center text-[#64748B] py-6">No closed job cards in period.</td></tr>}
          {(data.rows || []).map((r) => (
            <tr key={r.job_card_id}>
              <td className="font-mono-data font-medium">{r.number}</td>
              <td className="capitalize text-xs">{r.industry?.replace("_", " ")}</td>
              <td>{r.party}</td>
              <td className="font-mono-data text-xs">{r.date}</td>
              <td className="text-right font-mono-data">{fmtMoney(r.revenue, cur)}</td>
              <td className="text-right font-mono-data">{fmtMoney(r.estimated_cost, cur)}</td>
              <td className={`text-right font-mono-data font-semibold ${r.profit >= 0 ? "text-[#16A34A]" : "text-[#DC2626]"}`}>{fmtMoney(r.profit, cur)}</td>
              <td className="text-right font-mono-data">{r.margin_pct}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
